/**
 * A workspace's overrides, clamped like the application values they override
 * (§4.1) — which is also the configuration a shared project carries (§5.25).
 * Deliberately free of the agent settings' catalogue, so a page that validates
 * a project does not ship it.
 */
import { autoLandOverride } from './auto-land';
import { auditOverride } from './audit';
import { normalizeExtensions } from './extensions';
import { normalizeGitFormats } from './git-formats';
import { localServices } from './localservice';
import { rulesRepoRefOf } from '../rules/repository';
import { normalizeTargets } from './targets';
import { LIMITS } from './defaults';
import { EFFORT_IDS, MODEL_IDS } from './models';
import { NOTIFY_MODES, PROBE_MODES } from './modes';
import {
  AGENT_UPDATES,
  CONFIG_SOURCES,
  EGRESS_MODES,
  bool,
  branch,
  dockerfile,
  domains,
  hostServices,
  int,
  oneOf,
  privacyTerms
} from './primitives';
import type { WorkspaceConfig } from './types';

/**
 * The same validators, minus the fallback: an override that does not survive
 * validation becomes `null`, i.e. the workspace goes back to inheriting. Pinning
 * it to the app default instead would silently turn a rejected value into a
 * deliberate-looking choice.
 */
export function normalizeWorkspaceConfig(workspaceId: string, input: Partial<WorkspaceConfig>): WorkspaceConfig {
  return {
    workspaceId,

    defaultModel: oneOf(input.defaultModel, MODEL_IDS),
    defaultEffort: oneOf(input.defaultEffort, EFFORT_IDS),
    defaultSourceBranch: branch(input.defaultSourceBranch),
    archiveRetentionDays: int(input.archiveRetentionDays, LIMITS.archiveRetentionDays),

    sandboxBackend: oneOf(input.sandboxBackend, ['auto', 'local']) as WorkspaceConfig['sandboxBackend'],
    dockerfile: dockerfile(input.dockerfile),
    containerRuntime: oneOf(input.containerRuntime, ['none', 'docker']) as WorkspaceConfig['containerRuntime'],
    vmCpu: int(input.vmCpu, LIMITS.vmCpu),
    vmMemoryMb: int(input.vmMemoryMb, LIMITS.vmMemoryMb),
    vmDiskGb: int(input.vmDiskGb, LIMITS.vmDiskGb),
    dockerDiskGb: int(input.dockerDiskGb, LIMITS.dockerDiskGb),

    autonomous: bool(input.autonomous),
    agentConfigSource: oneOf(input.agentConfigSource, CONFIG_SOURCES) as WorkspaceConfig['agentConfigSource'],
    agentUpdate: oneOf(input.agentUpdate, AGENT_UPDATES) as WorkspaceConfig['agentUpdate'],
    idleHintMs: int(input.idleHintMs, LIMITS.idleHintMs),

    commitCoAuthoredBy: bool(input.commitCoAuthoredBy),
    privacyMask: bool(input.privacyMask),
    autoSync: bool(input.autoSync),
    gitEnabled: bool(input.gitEnabled),
    tunnelsEnabled: bool(input.tunnelsEnabled),
    agentPreview: bool(input.agentPreview),
    agentPreviewLogins: bool(input.agentPreviewLogins),
    agentProbes: oneOf(input.agentProbes, [...PROBE_MODES]) as WorkspaceConfig['agentProbes'],

    extraDomains: domains(input.extraDomains),
    blockedDomains: domains(input.blockedDomains),
    egressMode: oneOf(input.egressMode, EGRESS_MODES) as WorkspaceConfig['egressMode'],

    notifications: oneOf(input.notifications, NOTIFY_MODES) as WorkspaceConfig['notifications'],

    // A workspace stored before this list was named for services rather than
    // databases still has it under `hostDatabases`; both are read, so an
    // existing selection survives the rename instead of silently closing.
    hostServices: hostServices(input.hostServices ?? (input as { hostDatabases?: unknown }).hostDatabases),

    // Workspace-only, like the two lists above it: no application layer (§5.4).
    gitFormats: normalizeGitFormats(input.gitFormats),

    logRetentionMb: int(input.logRetentionMb, LIMITS.logRetentionMb),
    logBufferLines: int(input.logBufferLines, LIMITS.logBufferLines),

    // `null` is inheritance here as everywhere, and an **empty** document is a
    // real answer that is not the same thing: "this workspace adds nothing",
    // said deliberately, over an application that adds three (§5.26).
    agentExtensions: input.agentExtensions === null || input.agentExtensions === undefined
      ? null
      : normalizeExtensions(input.agentExtensions),

    // Only when there is one, on `subPath`'s bargain (§5.25): a key an older
    // install has never heard of makes it refuse the whole document.
    ...withLocalServices(input.localServices),
    ...withRulesRepository(input.rulesRepository),
    ...withAutoLand(input.autoLand),
    ...withAudit(input.audit),
    ...withPrivacyTerms(input.privacyTerms),
    ...withTargets(input.targets)
  };
}

/**
 * The keys a workspace configuration carries only when they hold something —
 * absent from an empty one, so `parse.ts` names them itself rather than reading
 * them off `emptyWorkspaceConfig`.
 */
export const SPARSE_CONFIG_KEYS = ['localServices', 'rulesRepository', 'autoLand', 'audit', 'privacyTerms', 'targets'] as const;

function withTargets(value: unknown): Pick<WorkspaceConfig, 'targets'> {
  const kept = normalizeTargets(value);
  return kept.length ? { targets: kept } : {};
}

function withLocalServices(value: unknown): Pick<WorkspaceConfig, 'localServices'> {
  const kept = localServices(value);
  return kept.length ? { localServices: kept } : {};
}

function withAutoLand(value: unknown): Pick<WorkspaceConfig, 'autoLand'> {
  const land = autoLandOverride(value);
  return land ? { autoLand: land } : {};
}

function withAudit(value: unknown): Pick<WorkspaceConfig, 'audit'> {
  const kept = auditOverride(value);
  return kept ? { audit: kept } : {};
}

function withPrivacyTerms(value: unknown): Pick<WorkspaceConfig, 'privacyTerms'> {
  const kept = privacyTerms(value) ?? [];
  return kept.length ? { privacyTerms: kept } : {};
}

function withRulesRepository(value: unknown): Pick<WorkspaceConfig, 'rulesRepository'> {
  const ref = rulesRepoRefOf(value);
  return ref ? { rulesRepository: ref } : {};
}

/** A workspace that overrides nothing — the shape a brand new one starts from. */
export function emptyWorkspaceConfig(workspaceId: string): WorkspaceConfig {
  return normalizeWorkspaceConfig(workspaceId, {});
}
